import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Steps, Tag, Divider, Alert, Spin, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  RocketTwoTone,
  StarTwoTone,
  CreditCardTwoTone,
  CheckCircleTwoTone,
  SafetyCertificateTwoTone,
  WalletTwoTone,
  ScanOutlined,
  DownloadOutlined,
  MailOutlined,
  ProjectTwoTone
} from '@ant-design/icons';
import { PLANS, DEFAULT_PLAN } from '../../data/plans';

const { Title, Paragraph, Text } = Typography;

interface OnboardingPageProps {
  token: string;
  API_URL: string;
}

export const OnboardingPage: React.FC<OnboardingPageProps> = ({ token, API_URL }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string>(DEFAULT_PLAN);
  const [qrisDataUrl, setQrisDataUrl] = useState<string | null>(null);
  const [checkoutInvoice, setCheckoutInvoice] = useState<string | null>(null);
  const [checkoutAmount, setCheckoutAmount] = useState<number>(PLANS.monthly.price);
  const [checkoutDays, setCheckoutDays] = useState<number>(30);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Clear onboarding flag so user won't be redirected back
  const dismissOnboarding = () => {
    localStorage.removeItem('show_onboarding');
  };

  // Clear onboarding flag on mount — user is already on the payment page
  useEffect(() => {
    dismissOnboarding();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/personal/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUserEmail(data.user?.email || '');
          setUserName(data.user?.name || '');
        }
      } catch (e) {
        console.error('Failed to fetch profile', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token, API_URL]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Spin size="large" />
        <span style={{ fontWeight: 800 }}>Memuat...</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '12px' }}>
      {/* Welcome Banner */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '6px 6px 0 #1a202c',
        marginBottom: '24px',
        background: 'linear-gradient(135deg, #fff9db 0%, #fff3bf 100%)',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '8px' }}><CheckCircleTwoTone style={{ fontSize: 56 }} /></div>
        <Title level={2} style={{ margin: 0, fontWeight: 900, color: '#1a202c' }}>
          Selamat Datang{userName ? `, ${userName}` : ''}!
        </Title>
        <Paragraph style={{ margin: '8px 0 0 0', fontSize: '1rem', fontWeight: 600, color: '#495057' }}>
          Akun BaZi Insights kamu <strong>berhasil dibuat</strong> dengan email <Tag color="blue" style={{ fontWeight: 700 }}>{userEmail}</Tag>
        </Paragraph>
      </Card>

      {/* Membership Info */}
      <Card style={{
        border: '3px solid #1a202c',
        borderRadius: '16px',
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px'
      }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '20px' }}>
          <Title level={3} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
            <RocketTwoTone twoToneColor="#faad14" style={{ marginRight: 10, fontSize: '28px' }} />
            Jadi Premium Member Sekarang
          </Title>
          <Paragraph style={{ margin: '8px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Akun kamu belum <strong>Premium</strong>. Langganan sekarang untuk mengakses semua fitur BaZi Insights tanpa batas!
          </Paragraph>
        </div>

        {/* Benefits */}
        <div style={{ marginBottom: '20px' }}>
          <Title level={5} style={{ fontWeight: 800, marginBottom: '12px' }}><StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 6 }} /> Benefit Premium:</Title>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <BenefitItem icon={<StarTwoTone twoToneColor="#fadb14" />} text="Bagan BaZi Lengkap — 4 pilar + 10 Dewa Ki" />
            <BenefitItem icon={<SafetyCertificateTwoTone twoToneColor="#1890ff" />} text="Cek Kecocokan — Pasangan, teman, atau rekan kerja" />
            <BenefitItem icon={<WalletTwoTone twoToneColor="#fa8c16" />} text="Bebas batas — Tidak ada kuota harian cek orang lain" />
          </div>
        </div>          {/* Plan Selection Cards */}
        <div style={{ marginBottom: '20px' }}>
          <Title level={5} style={{ fontWeight: 800, marginBottom: '12px' }}><CreditCardTwoTone twoToneColor="#1890ff" style={{ marginRight: 6 }} /> Pilih Paket:</Title>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {Object.entries(PLANS).map(([key, plan]) => {
              const isSelected = selectedPlan === key;
              return (
                <div
                  key={key}
                  onClick={() => setSelectedPlan(key)}
                  style={{
                    flex: 1,
                    minWidth: '140px',
                    padding: '14px 12px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    border: isSelected ? '3px solid #1a202c' : '2px solid #dee2e6',
                    background: isSelected ? '#fff9db' : '#fff',
                    boxShadow: isSelected ? '3px 3px 0 #1a202c' : 'none',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                >
                  {plan.badge && (
                    <span style={{
                      position: 'absolute',
                      top: '-10px',
                      right: '-6px',
                      background: '#fa5252',
                      color: '#fff',
                      padding: '2px 8px',
                      borderRadius: '20px',
                      fontSize: '10px',
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                    }}>
                      {plan.badge}
                    </span>
                  )}
                  <div style={{ fontWeight: 900, fontSize: '20px', color: '#e03131' }}>
                    Rp {plan.price.toLocaleString('id-ID')}
                  </div>
                  {plan.originalPrice && (
                    <div style={{ fontSize: '12px', color: '#adb5bd', textDecoration: 'line-through', marginTop: '1px' }}>
                      Rp {plan.originalPrice.toLocaleString('id-ID')}
                    </div>
                  )}
                  {plan.promoLabel && (
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#e03131', marginTop: '1px' }}>
                      🔥 {plan.promoLabel}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: '#868e96', marginTop: '2px' }}>
                    / {plan.desc}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#2b8a3e', marginTop: '4px' }}>
                    {plan.perHari}
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      border: isSelected ? '5px solid #1a202c' : '2px solid #dee2e6',
                      margin: '0 auto',
                      transition: 'all 0.2s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Button
            type="primary"
            block
            size="large"
            loading={checkoutLoading}
            onClick={async () => {
              setCheckoutLoading(true);
              try {
                const planKey = selectedPlan;
                const plan = PLANS[planKey as keyof typeof PLANS] || PLANS[DEFAULT_PLAN];
                const res = await fetch(`${API_URL}/api/v1/personal/subscription/checkout`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ plan: planKey })
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Gagal memulai pembayaran');
                setCheckoutInvoice(data.invoice_id);
                setCheckoutAmount(data.amount || plan.price);
                setCheckoutDays(data.days || plan.days);
                setQrisDataUrl(data.qris?.qr_data_url || null);
              } catch (err: any) {
                console.error('Checkout error:', err);
                // Fallback: just navigate to profile
                dismissOnboarding();
                navigate('/personal/profile');
              } finally {
                setCheckoutLoading(false);
              }
            }}
            style={{
              height: '50px',
              background: '#1a202c',
              border: '2px solid #1a202c',
              color: '#fab005',
              fontWeight: 900,
              fontSize: '1rem',
              borderRadius: '12px',
              boxShadow: '3px 3px 0 #fab005'
            }}
            icon={<CreditCardTwoTone twoToneColor="#faad14" style={{ fontSize: '20px' }} />}
          >
            Bayar & Jadi Premium Sekarang
          </Button>
        </div>
      </Card>

      {/* QRIS Payment Instructions — only shown after checkout (valid QR) */}
      {qrisDataUrl && (
        <Card style={{
          border: '3px solid #1a202c',
          borderRadius: '16px',
          boxShadow: '4px 4px 0 #1a202c',
          marginBottom: '24px',
          background: '#f8f9fa'
        }}>
          <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '20px' }}>
            <Title level={4} style={{ margin: 0, fontWeight: 900, display: 'flex', alignItems: 'center' }}>
              <ScanOutlined style={{ marginRight: 10, fontSize: '24px', color: '#1a202c' }} />
              Cara Bayar via QRIS
            </Title>
            <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
              Scan QRIS di bawah langsung dari aplikasi pembayaran kamu — nominal <strong>Rp {checkoutAmount.toLocaleString('id-ID')}</strong> sudah terisi otomatis!{' '}
              Akses premium selama <strong>{checkoutDays} hari</strong>.
            </Paragraph>
          </div>

          {/* QRIS Image */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px',
            padding: '20px',
            background: '#fff',
            border: '2px dashed #1a202c',
            borderRadius: '12px'
          }}>
            <img
              src={qrisDataUrl}
              alt="QRIS Pembayaran BaZi Insights"
              style={{
                width: '200px',
                height: '200px',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
            <div style={{ marginTop: '12px' }}>
              <Button
                type="default"
                size="small"
                onClick={async () => {
                  try {
                    const a = document.createElement('a');
                    a.href = qrisDataUrl!;
                    a.download = `qris-bazi-${checkoutInvoice || 'payment'}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } catch (e) {
                    console.error('Gagal download QRIS', e);
                  }
                }}
                style={{
                  border: '2px solid #1a202c',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '13px',
                  boxShadow: '1px 1px 0 #1a202c'
                }}
                icon={<DownloadOutlined style={{ fontSize: 14 }} />}
              >
                Download QRIS
              </Button>
            </div>
          </div>

          {/* Invoice info */}
          {checkoutInvoice && (
            <div style={{
              background: '#fff3bf',
              border: '2px solid #fab005',
              borderRadius: '8px',
              padding: '8px 12px',
              marginBottom: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px',
              fontWeight: 600
            }}>
              <span>Invoice: <span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{checkoutInvoice}</span></span>
              <Tag color="gold" style={{ fontWeight: 700, margin: 0 }}>Menunggu Pembayaran</Tag>
            </div>
          )}

          {/* Steps */}
          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={[
              {
                title: <Text strong>Buka Aplikasi Dompet Digital</Text>,
                description: 'Buka GoPay, OVO, DANA, ShopeePay, atau aplikasi perbankan Anda.',
                icon: <WalletTwoTone twoToneColor="#1890ff" />
              },
              {
                title: <Text strong>Scan QRIS Langsung</Text>,
                description: `Pilih fitur Scan QR, lalu scan QRIS di atas — nominal Rp ${checkoutAmount.toLocaleString('id-ID')} sudah terisi otomatis.`,
                icon: <ScanOutlined style={{ color: '#52c41a' }} />
              },
              {
                title: <Text strong>Selesaikan & Konfirmasi</Text>,
                description: (
                  <span>
                    Selesaikan pembayaran, lalu klik tombol <strong>'Saya Sudah Bayar'</strong> di bawah. Admin akan verifikasi dalam 1x24 jam.{' '}
                    <Tag color="blue" style={{ fontWeight: 700, marginTop: 4 }}>admin@bazi.com</Tag>
                  </span>
                ),
                icon: <RocketTwoTone twoToneColor="#722ed1" />
              }
            ]}
          />

          {/* Confirm Payment Button */}
          {checkoutInvoice && (
            <div style={{ marginTop: '20px' }}>
              <Button
                block
                size="large"
                loading={confirmLoading}
                onClick={async () => {
                  setConfirmLoading(true);
                  try {
                    const res = await fetch(`${API_URL}/api/v1/personal/subscription/checkout/${checkoutInvoice}/confirm`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({})
                    });
                    if (!res.ok) {
                      const err = await res.json();
                      throw new Error(err.error || 'Gagal');
                    }
                    message.success('Pembayaran telah dilaporkan! Admin akan memverifikasi dalam 1x24 jam.');
                  } catch (err: any) {
                    message.error(err.message || 'Gagal melaporkan pembayaran. Silakan hubungi admin.');
                  } finally {
                    setConfirmLoading(false);
                  }
                }}
                style={{
                  height: '50px',
                  background: '#1a202c',
                  border: '2px solid #1a202c',
                  color: '#fab005',
                  fontWeight: 900,
                  fontSize: '1rem',
                  borderRadius: '12px',
                  boxShadow: '3px 3px 0 #fab005'
                }}
                icon={<ScanOutlined />}
              >
                Saya Sudah Bayar
              </Button>
            </div>
          )}

          <Divider style={{ borderTop: '2px solid #1a202c', margin: '20px 0' }} />

          <Alert
            message={<span style={{ fontWeight: 800 }}><MailOutlined style={{ marginRight: 6 }} /> Butuh Bantuan?</span>}
            description={
              <span style={{ fontWeight: 600 }}>
                Jika sudah melakukan pembayaran tapi membership belum aktif dalam 1x24 jam, hubungi{' '}
                <strong>admin@bazi.com</strong> atau kirim bukti transfer melalui email.
              </span>
            }
            type="info"
            showIcon
            style={{ border: '2px solid #1a202c', borderRadius: '8px', background: '#e7f5ff' }}
          />
        </Card>
      )}

      {/* Informational: Profile saved — shown before checkout */}
      {!qrisDataUrl && !checkoutLoading && (
        <Alert
          message={<span style={{ fontWeight: 800 }}><CheckCircleTwoTone twoToneColor="#52c41a" style={{ marginRight: 6 }} /> Profil Berhasil Tersimpan</span>}
          description="Data tanggal lahir & jam lahirmu sudah tersimpan. Sekarang klik 'Bayar & Jadi Premium Sekarang' untuk mengaktifkan membership Premium."
          type="success"
          showIcon
          style={{ marginBottom: '20px', border: '2px solid #2b8a3e', borderRadius: '8px' }}
        />
      )}
    </div>
  );
};

// Helper component
const BenefitItem: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <span style={{ fontSize: '18px' }}>{icon}</span>
    <Text style={{ fontWeight: 600, fontSize: '14px' }}>{text}</Text>
  </div>
);

export default OnboardingPage;
